import React from "react";
import { ReactSpreadsheetImport } from "react-spreadsheet-import";
import type { ImportResult, RowHook } from "react-spreadsheet-import";
import "./RosterUploader.scss";
import moment from "moment";
import {
  formatErrorString,
  IndividualAddress1Validator,
  individualAddress2Validator,
  emailValidator,
  INVALID_PHONE_NUMBER,
  INVALID_SSN_ERROR,
  NAME_LENGTH_ERROR,
  NAME_MESSAGE,
  noWhitespaceRegex,
  PRINTABLE_ASCII_MESSAGE,
  stateValidator,
  testFirstLastName,
  testName,
  testPhoneNumber,
  testPrintableAsciiCharacters,
  testSsn,
  whiteSpaceError,
} from "utils/fieldValidators";
import { matchColumns, selectHeader, validateRows } from "statics/rosterSteps";
import { each, map, mapValues, size, trim } from "lodash";

import * as yup from "yup";
const DOB_FORMAT = "YYYY-MM-DD";

const schema = yup.object({
  firstName: yup
    .string()
    .label("First")
    .matches(noWhitespaceRegex, whiteSpaceError)
    .required()
    .test("is-valid-name", NAME_MESSAGE, testName)
    .min(1, "Must be at least one character.")
    .max(20)
    .test("first-last-name-length", NAME_LENGTH_ERROR, testFirstLastName),
  lastName: yup
    .string()
    .label("Last")
    .matches(noWhitespaceRegex, whiteSpaceError)
    .required()
    .test("is-valid-name", NAME_MESSAGE, testName)
    .min(1, "Must be at least one character.")
    .max(20, "Must be less than 20 characters.")
    .test("first-last-name-length", NAME_LENGTH_ERROR, testFirstLastName),
  email: emailValidator().required(),
  dob: yup
    .string()
    .label("Date Of Birth")
    .required()
    .test("dob", "dob must be a valid date string YYYY-MM-DD.", (value) => {
      return moment(value, DOB_FORMAT, true).isValid();
    })
    .test(
      "dob",
      "Employee must be at least 18 to create an account.",
      (value) => {
        return moment().diff(moment(value), "years") >= 18;
      }
    )
    .test(
      "dob",
      "Employee must be under 100 years old to create an account.",
      (value) => {
        return moment().diff(moment(value), "years") < 100;
      }
    ),
  ssn: yup
    .string()
    .trim()
    .required()
    .label("Social Security Number")
    // PRESERVED QUIRK: yup's .test() takes 1-3 args; the trailing
    // (value) => testSsn(value, true) is a DEAD 4th argument that yup ignores.
    // Roster CSVs may rely on the accidental allowNoDash behavior this implies,
    // so it is kept verbatim and NOT fixed. The param is annotated only to keep
    // the call type-safe; the lambda body is unchanged. (Suppresses TS2554.)
    // @ts-expect-error dead 4th arg preserved intentionally
    .test("ssn", INVALID_SSN_ERROR, testSsn, (value: string | undefined) =>
      testSsn(value, true)
    ),
  address1: IndividualAddress1Validator,
  address2: individualAddress2Validator,
  city: yup
    .string()
    .trim()
    .label("City")
    .required()
    .test("city", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .max(50),
  state: stateValidator,
  postalCode: yup
    .string()
    .trim()
    .label("Zip")
    .required()
    .test("postalCode", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test(
      "postalCode",
      "Postal Code must be five digits",
      (val) => size(val) === 5
    ),

  phoneNumber: yup
    .string()
    .trim()
    .label("Phone")
    .required()
    .test("phone", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test("us-phone", INVALID_PHONE_NUMBER, testPhoneNumber)
    .max(15),
});

// email	ssn	firstName	lastName	address1	address2	city	state	postalCode	phoneNumber	dob	location	department

interface RosterUploaderProps {
  // .func — no obvious precise handler signature; receives the validated rows
  onSubmit?: (...args: unknown[]) => void;
  onClose?: (...args: unknown[]) => void;
  setActiveStep?: (...args: unknown[]) => void;
  isOpen?: boolean;
}

// This component holds no instance state (no this.state / this.setState).
type RosterUploaderState = Record<string, never>;

export default class RosterUploader extends React.PureComponent<
  RosterUploaderProps,
  RosterUploaderState
> {
  _onSubmit = (data: ImportResult) => {
    // non-null assertion preserves the original behavior: onSubmit is an
    // optional prop and was called unguarded (would throw if undefined).
    this.props.onSubmit!(data.validData);
  };

  _rowValidator: RowHook = (data, addError) => {
    const newDataObj = mapValues(data, trim);
    const dob = moment(newDataObj.dob);
    // if dob is a valid date string we should auto-format it into the format we expect
    if (dob.isValid()) {
      newDataObj.dob = dob.format(DOB_FORMAT);
    }

    try {
      schema.validateSync(newDataObj, {
        abortEarly: false,
      });
      return newDataObj;
    } catch (err) {
      // schema.validateSync throws a yup.ValidationError (TS catch binds unknown)
      each((err as yup.ValidationError).inner, (pathErrors) => {
        const errors = map(pathErrors.errors, (error) =>
          formatErrorString(error)
        );

        const joinedErrors = errors.join(" ");
        // yup types path as optional, but inner errors always carry one;
        // assertion preserves the original unguarded pathErrors.path usage.
        addError(pathErrors.path!, {
          message: joinedErrors,
          level: "error",
        });
      });
      return newDataObj;
    }
  };

  render() {
    // 502 to account for header row and potentially description row
    const maxRows = 502;
    return (
      <ReactSpreadsheetImport
        inline
        hideStepTitles
        hideStepper
        isOpen={true}
        maxRecords={maxRows}
        onSubmit={this._onSubmit}
        onClose={this.props.onClose}
        allowInvalidSubmit={false}
        rowHook={this._rowValidator}
        uploadStepHook={(data) => {
          // non-null assertions preserve original behavior: setActiveStep is an
          // optional prop and was called unguarded in every step hook.
          this.props.setActiveStep!(selectHeader);
          return data;
        }}
        selectHeaderStepHook={(headerValues, data) => {
          this.props.setActiveStep!(matchColumns);
          return { data, headerValues };
        }}
        matchColumnsStepHook={(values) => {
          this.props.setActiveStep!(validateRows);
          return values;
        }}
        translations={{
          alerts: {
            unmatchedRequiredFields: {
              bodyText:
                "There are required columns that are not matched, please match all columns.",
            },
          },
        }}
        fields={[
          {
            label: "Email",
            key: "email",
            required: true,
            unique: true,
            example: "user@company.com",
            alternateMatches: ["emailaddress", "personalemail", "homeemail"],
          },
          {
            label: "SSN",
            key: "ssn",
            required: true,
            unique: true,
            alternateMatches: [
              "social security number",
              "social",
              "socialSecurityNumber",
              "socialsecuritynumber",
              "socialsecurity#",
            ],
            example: "111-11-1111",
          },
          {
            label: "First Name",
            key: "firstName",
            required: true,
            alternateMatches: ["first"],
            example: "John",
          },
          {
            label: "Last Name",
            key: "lastName",
            required: true,
            alternateMatches: ["last"],
            example: "Doe",
          },
          {
            label: "Address 1",
            key: "address1",
            required: true,
            alternateMatches: [
              "street1",
              "line1",
              "addressline1",
              "streetaddress",
            ],
            example: "123 main st",
          },
          {
            label: "Address 2",
            key: "address2",
            alternateMatches: [
              "street2",
              "line2",
              "addressline2",
              "streetaddress2",
            ],
            example: "apt. 1",
          },
          {
            label: "City",
            key: "city",
            required: true,
            example: "Portland",
          },
          {
            label: "State",
            key: "state",
            required: true,
            example: "OR",
          },
          {
            label: "Postal Code",
            key: "postalCode",
            required: true,
            alternateMatches: ["zipcode", "zip", "postalcode"],
            example: "97209",
          },
          {
            label: "Phone Number",
            key: "phoneNumber",
            required: true,
            alternateMatches: [
              "phone",
              "mobile",
              "personalmobile",
              "cellphone",
              "homephone",
            ],
            example: "555-555-5555",
          },
          {
            label: "Date of Birth",
            key: "dob",
            required: true,
            alternateMatches: ["dateofbirth", "birthdate"],
            example: "1950-12-31",
          },
          {
            label: "Location",
            key: "location",
            example: "Denver Region",
          },
          {
            label: "Department",
            key: "department",
            example: "Engineering",
          },
        ]}
      />
    );
  }
}
